import MojitoCore from 'mojito/core';
import Image from 'presentation/components/image/index.js';
import AbsolutePane from 'presentation/components/absolute-pane/index.js';
import ImageButton from 'presentation/components/image-button/index.js';
import Text from 'presentation/components/text/index.js';
import Popup from 'presentation/components/popup/index.js';
import Button from 'presentation/components/button/index.js';
import { useCombinedRefs, useComponentVisibility } from 'presentation/hooks';
import { isEmpty } from 'mojito/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const ObjectUtils = MojitoCore.Base.objUtils;
const classUtils = MojitoCore.Base.classUtils;
const { escapePixels } = MojitoCore.Base.NumberUtils;

const Tooltip = props => {
    const {
        mojitoTools,
        onClose,
        text,
        linkText,
        onClick,
        elementRef: elementRefProp,
        arrowRef,
        onOutsideClick,
        onLinkClick,
        showTooltipButton,
        class: className,
    } = props;
    const { config } = mojitoTools;
    const onCloseClick = event => {
        event.stopPropagation();
        onClose();
    };

    const { elementRef, delta } = usePopupPosition(config.autoAdjustablePosition);
    const popupConfig = usePopupConfig(config.popup, delta, config.autoAdjustingOffset);
    const popupRef = useCombinedRefs(elementRefProp, elementRef);
    // We need to use own outside click handler because on a popup it is implemented using an overlay shim with position: fixed,
    // which causes issues when popup is getting auto adjusted to fit into the viewport. The shim becomes fixed not relative to the view port but relative to the adjusted popup position.
    // Due to that click outside detection does not work correctly.
    useClickOutside(config.captureOutsideClicks ? onOutsideClick : undefined);

    const hasImage = !isEmpty(config.image.src);
    return (
        <Popup
            elementRef={popupRef}
            arrowRef={arrowRef}
            config={popupConfig}
            visible={true}
            captureOutsideClicks={false}
            class={classUtils.classNames('ta-Tooltip', className)}
        >
            <Button config={config.clickableContainer} onClick={onClick}>
                {hasImage && <Image config={config.image} class="ta-image" />}
                <Text config={config.textLabel}>{text}</Text>
                {showTooltipButton && (
                    <Button config={config.linkButton} onClick={onLinkClick} class="ta-linkButton">
                        {linkText}
                    </Button>
                )}
                {config.showCloseButton && (
                    <AbsolutePane config={config.closeButtonContainer}>
                        <ImageButton
                            class="ta-tooltip-closeButton"
                            onClick={onCloseClick}
                            config={config.closeButton}
                        />
                    </AbsolutePane>
                )}
            </Button>
        </Popup>
    );
};

// Hook to adjust the popup configuration based on the delta value.
// If delta is non-zero, it applies translateX to popup to move it back to te viewport. It will also adjust the arrow position.
const usePopupConfig = (config, delta, offset) => {
    const adjustedConfigRef = useRef();
    const adjustedConfig = useMemo(
        () =>
            !isNaN(delta) && delta !== 0
                ? makePositionAdjustment(config, delta, offset)
                : undefined,
        [config, delta, offset]
    );

    if (adjustedConfig) {
        adjustedConfigRef.current = adjustedConfig;
    }
    return adjustedConfigRef.current || config;
};

// Hook to determine if the tooltip position needs to be adjusted to fit within the viewport.
// If it should be adjusted, returns a delta value to shift the tooltip horizontally.
const usePopupPosition = adjustable => {
    const [delta, setDelta] = useState(0);

    const onShowElement = useCallback(entry => {
        // The logic below will detect if tooltip is being intercepted and clipped by its parent.
        // This can happen if parent has overflow: hidden for example. In this case we will adjust popup position to fit into viewable area.
        const { left: intersectsLeft, right: intersectsRight } = entry.intersectionRect;
        const { left: rectLeft, right: rectRight } = entry.boundingClientRect;
        const leftClipping = intersectsLeft > rectLeft;
        const rightClipping = intersectsRight < rectRight;
        if (leftClipping || rightClipping) {
            const offset = leftClipping ? intersectsLeft - rectLeft : intersectsRight - rectRight;
            setDelta(offset);
        } else {
            setDelta(0);
        }
    }, []);

    const elementRef = useComponentVisibility(onShowElement);
    if (!adjustable) {
        return {};
    }

    return { elementRef, delta };
};

const useClickOutside = handler => {
    useEffect(() => {
        handler && document.addEventListener('click', handler, true);
        return () => {
            document.removeEventListener('click', handler, true);
        };
    }, [handler]);
};

const makePositionAdjustment = (config, delta, offset) => {
    const arrowOffset = config.arrowheadOffset?.includes('calc')
        ? NaN
        : escapePixels(config.arrowheadOffset);
    const multiplier = delta < 0 ? -1 : 1;
    const padding = escapePixels(offset) * multiplier;
    const totalOffset = delta + padding;
    const arrowConfig = !isNaN(arrowOffset)
        ? { arrowheadOffset: toPixels(arrowOffset - totalOffset) }
        : {};
    const positionConfig = {
        container: {
            style: {
                transform: delta ? `translateX(${toPixels(totalOffset)})` : undefined,
                zIndex: delta ? 1 : 'auto',
            },
        },
        ...arrowConfig,
    };
    return ObjectUtils.merge(config, positionConfig);
};

const toPixels = value => (typeof value === 'number' ? `${value}px` : value);

export default Tooltip;
